import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Fab
} from '@mui/material';
import {
  ArrowBack,
  CloudUpload,
  ExpandMore,
  Restaurant as RestaurantIcon,
  MenuBook,
  AttachMoney,
  Chat as ChatIcon
} from '@mui/icons-material';
import { restaurantApi, menuApi, handleApiError } from '../services/api';
import ChatBot from '../components/ChatBot';
import type { Restaurant, MenuItem as MenuItemType } from '../types';

const MenuView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState<'pdf' | 'image'>('pdf');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [chatOpen, setChatOpen] = useState(true); // Auto-open chat for chat-first experience

  useEffect(() => {
    if (id) {
      loadData(id);
    }
  }, [id]);

  const loadData = async (restaurantId: string) => {
    try {
      setLoading(true);
      const [restaurantData, menuData] = await Promise.all([
        restaurantApi.getRestaurant(restaurantId),
        menuApi.getMenuItems(restaurantId)
      ]);
      setRestaurant(restaurantData);
      setMenuItems(menuData);
      setError('');
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile || !id) return;

    try {
      setUploading(true);
      const result = await menuApi.uploadMenu(id, selectedFile, uploadType);
      
      // Refresh menu data after successful upload
      await loadData(id);
      
      setUploadDialogOpen(false);
      setSelectedFile(null);
      
      // Show success message
      console.log('Upload successful:', result);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const formatPrice = (price: number) => {
    return price > 0 ? `$${price.toFixed(2)}` : 'Market Price';
  };

  // Group menu items by category
  const groupedItems = menuItems.reduce((acc, item) => {
    const category = item.category_name || item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, MenuItemType[]>);

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!restaurant) {
    return (
      <Container>
        <Alert severity="warning" sx={{ mt: 4 }}>
          Restaurant not found
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mb={4}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/restaurant/${id}`)}
          sx={{ mb: 2 }}
        >
          Back to Restaurant
        </Button>
        
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <MenuBook sx={{ mr: 2, fontSize: 40 }} color="primary" />
            <Typography variant="h1">
              {restaurant.name} Menu
            </Typography>
          </Box>
          
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<ChatIcon />}
              onClick={() => navigate(`/restaurant/${id}`)}
            >
              Chat with AI
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload Menu
            </Button>
          </Box>
        </Box>
        
        <Typography variant="subtitle1" color="text.secondary">
          {Object.keys(groupedItems).length} categories • {menuItems.length} items
        </Typography>
      </Box>

      {Object.keys(groupedItems).length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <CardContent>
            <RestaurantIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No menu items yet
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              Upload a menu PDF or image to get started
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload Menu
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {Object.entries(groupedItems).map(([category, items]) => (
            <Accordion key={category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box display="flex" alignItems="center" width="100%">
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {category}
                  </Typography>
                  <Chip
                    label={`${items.length} items`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List disablePadding>
                  {items.map((item, index) => (
                    <ListItem key={item.menu_item_id} divider={index < items.length - 1}>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="start">
                            <Typography variant="subtitle1" component="span">
                              {item.name}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <AttachMoney sx={{ fontSize: 16, color: 'success.main' }} />
                              <Typography variant="subtitle1" color="success.main" fontWeight="bold">
                                {formatPrice(item.price)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Stack spacing={1} sx={{ mt: 1 }}>
                            {item.description && (
                              <Typography variant="body2" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                            
                            {(item.allergens.length > 0 || item.dietary.length > 0) && (
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {item.allergens.map((allergen: string) => (
                                  <Chip
                                    key={allergen}
                                    label={allergen}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                  />
                                ))}
                                {item.dietary.map((diet: string) => (
                                  <Chip
                                    key={diet}
                                    label={diet}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                            
                            {item.ingredients.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Ingredients: {item.ingredients.join(', ')}
                              </Typography>
                            )}
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      <Fab
        color="primary"
        aria-label="upload"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setUploadDialogOpen(true)}
      >
        <CloudUpload />
      </Fab>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Menu</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Menu Type</InputLabel>
              <Select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as 'pdf' | 'image')}
                label="Menu Type"
              >
                <MenuItem value="pdf">PDF Document</MenuItem>
                <MenuItem value="image">Image (JPG, PNG)</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <input
                type="file"
                accept={uploadType === 'pdf' ? '.pdf' : 'image/*'}
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {selectedFile ? selectedFile.name : `Choose ${uploadType.toUpperCase()} file`}
                </Button>
              </label>
            </Box>

            {selectedFile && (
              <Alert severity="info">
                File selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload & Process'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ChatBot Component */}
      <ChatBot
        restaurant={restaurant}
        isOpen={chatOpen}
        onToggle={() => setChatOpen(!chatOpen)}
      />
    </Container>
  );
};

export default MenuView; 